import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { MapPin, ArrowLeft, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/seo/Seo";

export default function ProjectDetail() {
  const { slug } = useParams();
  const location = useLocation();
  // Optimistic render: use the project passed via Link state (from the card click),
  // then refresh silently from the API. This makes navigation feel instant.
  const preloaded = location.state?.project && location.state.project.slug === slug
    ? location.state.project
    : null;
  const [p, setP] = useState(preloaded);
  const [loading, setLoading] = useState(!preloaded);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    // Reset scroll position when opening a case study
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
    let alive = true;
    api.get(`/projects/${slug}`)
      .then((r) => { if (alive) setP(r.data); })
      .catch(() => { if (alive && !preloaded) setNotFound(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug]);

  // Phase 4.0.1 — when a project is also published as a case study, the
  // canonical URL moves to /case-studies/{slug}. We do a SPA replace redirect
  // here (the authoritative 301 is a Cloudflare edge rule — see TS-06).
  if (p && p.publish_as_case_study && p.publication_status === "PUBLISHED") {
    return <Navigate to={`/case-studies/${p.slug}`} replace />;
  }
  // Standard projects: the auto-synced /portfolio/ page (same record) is canonical.
  if (p && p.linked_portfolio_slug) {
    return <Navigate to={`/portfolio/${p.linked_portfolio_slug}`} replace />;
  }

  if (!p && loading) {
    return <div className="max-w-5xl mx-auto px-6 py-32 text-center text-muted-foreground">Loading…</div>;
  }
  if (notFound || !p) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-32 text-center" data-testid="project-not-found">
        <Seo title="Project not found" noindex />
        <h1 className="font-heading font-bold text-3xl">Case study not found</h1>
        <Link to="/#featured-projects-section" className="mt-6 inline-flex items-center gap-2 text-primary">
          <ArrowLeft className="w-4 h-4" /> Back to projects
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="project-detail-page">
      <Seo title={p.name} description={p.tagline} />
      {/* Hero */}
      <section className="relative pt-16 md:pt-20 pb-14">
        <div className="max-w-6xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground hover:text-primary transition-colors" data-testid="project-back">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to home
          </Link>

          <div className="mt-6 flex items-center gap-3 text-xs">
            <span className="px-2.5 py-1 rounded-full bg-primary/10 text-primary font-mono uppercase tracking-[0.2em]">
              {p.industry}
            </span>
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <MapPin className="w-3 h-3" /> {p.country}
            </span>
          </div>

          <h1 className="mt-5 font-heading font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]" data-testid="project-title">
            {p.name}
          </h1>
          <p className="mt-5 text-lg md:text-xl text-muted-foreground max-w-3xl leading-relaxed">
            {p.tagline}
          </p>

          <div className="mt-10 rounded-3xl overflow-hidden border border-border">
            <img
              src={p.cover_image}
              alt={p.name}
              fetchPriority="high"
              className="w-full aspect-[16/8] object-cover"
            />
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="relative pb-20">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-12 gap-12">
          {/* Left: narrative */}
          <div className="lg:col-span-8 space-y-14">
            {p.problem && (
              <div data-testid="project-problem">
                <div className="text-xs font-mono uppercase tracking-[0.25em] text-muted-foreground">01 · Problem</div>
                <h2 className="mt-3 font-heading font-bold text-2xl md:text-3xl leading-tight">The problem</h2>
                <p className="mt-4 text-base md:text-lg leading-relaxed text-foreground/85 whitespace-pre-line">{p.problem}</p>
              </div>
            )}

            {p.approach && (
              <div data-testid="project-approach">
                <div className="text-xs font-mono uppercase tracking-[0.25em] text-muted-foreground">02 · Approach</div>
                <h2 className="mt-3 font-heading font-bold text-2xl md:text-3xl leading-tight">Our approach</h2>
                <p className="mt-4 text-base md:text-lg leading-relaxed text-foreground/85 whitespace-pre-line">{p.approach}</p>
              </div>
            )}

            {p.results?.length > 0 && (
              <div data-testid="project-results">
                <div className="text-xs font-mono uppercase tracking-[0.25em] text-muted-foreground">03 · Results</div>
                <h2 className="mt-3 font-heading font-bold text-2xl md:text-3xl leading-tight">Measurable results</h2>
                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                  {p.results.map((r, i) => (
                    <div key={i} className="card-clean p-6">
                      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
                        {r.metric}
                      </div>
                      <div className="mt-2 font-heading font-black text-3xl md:text-4xl text-primary">
                        {r.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: meta */}
          <aside className="lg:col-span-4 space-y-6">
            {p.client && (
              <div className="card-clean p-6">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Client</div>
                <div className="mt-2 font-heading font-bold text-lg">{p.client}</div>
              </div>
            )}
            {p.tech_stack?.length > 0 && (
              <div className="card-clean p-6" data-testid="project-tech-stack">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Tech Stack</div>
                <ul className="mt-3 space-y-2">
                  {p.tech_stack.map((t) => (
                    <li key={t} className="inline-flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-3.5 h-3.5 text-primary" /> {t}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <button
              onClick={() => window.dispatchEvent(new CustomEvent("open-quote-drawer"))}
              className="block w-full text-left card-clean p-6 hover:border-primary/40 transition-colors group"
              data-testid="project-cta-quote"
            >
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Ready to talk?</div>
              <div className="mt-2 font-heading font-bold text-lg">
                Build something like this →
              </div>
              <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                Share a rough spec and we&apos;ll come back within 48 hours with scope, timeline and price.
              </p>
            </button>
          </aside>
        </div>
      </section>
    </div>
  );
}
